import { useState, useContext } from "react";
import { UserContext } from "../context/UserContext";
import api from "../api";

// Social-Links werden jetzt dynamisch als Array von Objekten gespeichert

export default function EditProfile({ user, onSave }) {
  const cardStyle = {
    background: 'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
    padding: 16,
  };
  const sectionTitleStyle = { fontSize: 18, fontWeight: 800, margin: 0 };
  const labelStyle = { display: 'block', fontSize: 13, opacity: 0.85, marginBottom: 6 };
  const hintStyle = { fontSize: 12, opacity: 0.75, lineHeight: 1.45 };
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(0,0,0,0.22)',
    color: '#fff',
    outline: 'none',
  };
  const selectStyle = { ...inputStyle, padding: '10px 12px' };
  const textareaStyle = { ...inputStyle, minHeight: 110, resize: 'vertical' };
  const subtleDivider = { borderTop: '1px solid rgba(255,255,255,0.10)', margin: '12px 0' };

  // Preview-Helfer: nur Query anhängen, KEIN zusätzliches /api Präfix mehr
  const toPreviewUrl = (raw) => {
    if (!raw) return '';
    // Cache-Busting für die lokale Vorschau (nicht im globalen User speichern)
    const ts = Date.now();
    // Falls schon ein Query existiert, nur &t= ergänzen
    if (/\?/u.test(raw)) return raw + '&t=' + ts;
    return raw + '?t=' + ts;
  };

  const [form, setForm] = useState({
    displayName: user.displayName || "",
    bio: user.bio || "",
    avatarUrl: user.avatarUrl || "",
    socialLinks: Array.isArray(user.socialLinks) ? user.socialLinks : [],
    settings: typeof user.settings === 'object' && user.settings ? user.settings : {}
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user.avatarUrl ? toPreviewUrl(user.avatarUrl) : "");
  const [avatarError, setAvatarError] = useState("");
  const { setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteChecked, setDeleteChecked] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (field === "avatarUrl") {
      // Vorschau aktualisieren (roh + Cache-Bust), nicht mutieren zu /api/... hier
      setAvatarPreview(value ? toPreviewUrl(value) : "");
      setAvatarFile(null);
      setAvatarError("");
    }
  };
  // Avatar file validation
  const validateAvatar = file => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (!allowedTypes.includes(file.type)) {
      return "Nur JPG, PNG, GIF, WEBP erlaubt.";
    }
    if (file.size > maxSize) {
      return "Datei zu groß (max. 2MB).";
    }
    return "";
  };

  const handleAvatarFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const error = validateAvatar(file);
    if (error) {
      setAvatarError(error);
      setAvatarFile(null);
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file)); // temporäre Blob-URL
    setForm(f => ({ ...f, avatarUrl: "" })); // Clear URL if file chosen
    setAvatarError("");
  };

  const handleAvatarDrop = e => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const error = validateAvatar(file);
    if (error) {
      setAvatarError(error);
      setAvatarFile(null);
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setForm(f => ({ ...f, avatarUrl: "" }));
    setAvatarError("");
  };

  const handleAvatarRemove = () => {
    setAvatarFile(null);
    setAvatarPreview("");
    setForm(f => ({ ...f, avatarUrl: "" }));
    setAvatarError("");
  };

  // Social-Links: Name/URL ändern
  // Social-Links: Name/URL/Checkbox ändern
  const handleSocialChange = (idx, field, value) => {
    setForm(f => ({
      ...f,
      socialLinks: f.socialLinks.map((link, i) => {
        if (i !== idx) return link;
        // Wenn Favicon-Haken entfernt wird, icon löschen
        if (field === "useFavicon" && !value && link.icon) {
          const { icon: Icon, ...rest } = link;
          return { ...rest, [field]: value };
        }
        return { ...link, [field]: value };
      })
    }));
  };

  // Social-Links: Neues Feld hinzufügen
  const handleAddSocial = () => {
    setForm(f => ({
      ...f,
      socialLinks: [...f.socialLinks, { name: "", url: "", useFavicon: false }]
    }));
  };

  // Social-Links: Feld entfernen
  const handleRemoveSocial = idx => {
    setForm(f => ({
      ...f,
      socialLinks: f.socialLinks.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
  setLoading(true);
  setError("");
  setSuccess("");
    try {
      let avatarUrl = form.avatarUrl;
      // If file selected, upload first
      if (avatarFile) {
        const fd = new FormData();
        fd.append("avatar", avatarFile);
        const uploadRes = await api.post("/admin/profile/avatar", fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        avatarUrl = uploadRes.data.avatarUrl;
      }
      // Prüfe, ob Social-Links mit useFavicon vorhanden sind
      let faviconMsg = "";
      const faviconLinks = form.socialLinks.filter(l => l.useFavicon && l.url);
      let socialLinksToSave = form.socialLinks;
      if (faviconLinks.length > 0) {
        try {
          const favRes = await api.patch("/admin/favicon-test", { ...form, avatarUrl });
          if (favRes.data.faviconResults && Array.isArray(favRes.data.faviconResults)) {
            faviconMsg = favRes.data.faviconResults.map(r => `${r.url}: ${r.success ? '✔️' : '❌'} ${r.message}`).join('\n');
          }
          // Social-Links mit gesetztem icon übernehmen
          if (favRes.data.user && Array.isArray(favRes.data.user.socialLinks)) {
            socialLinksToSave = favRes.data.user.socialLinks;
          }
        } catch (e) {
          faviconMsg = "Favicon-Fehler: " + (e.response?.data?.error || e.message);
        }
      }
  // Prepare settings with allowed keys only
  const socialViewMode = (form.settings && typeof form.settings.socialViewMode === 'string') ? form.settings.socialViewMode : undefined;
  const visVals = ['public','members','admin'];
  const profileVisibility = (form.settings && typeof form.settings.profileVisibility === 'string' && visVals.includes(form.settings.profileVisibility)) ? form.settings.profileVisibility : undefined;
  const membershipVisibility = (form.settings && typeof form.settings.membershipVisibility === 'string' && visVals.includes(form.settings.membershipVisibility)) ? form.settings.membershipVisibility : undefined;
  const importTwitchScheduleToSupportPlan = !!(form.settings && typeof form.settings.importTwitchScheduleToSupportPlan === 'boolean' ? form.settings.importTwitchScheduleToSupportPlan : false);
  // Session Präferenzen aus Formular (Fallbacks)
  const sessionStay = !!(form.settings?.session?.stayLoggedIn);
  const autoMinutesRaw = form.settings?.session?.autoLogoutMinutes;
  const autoMinutes = (typeof autoMinutesRaw === 'number' && !isNaN(autoMinutesRaw)) ? autoMinutesRaw : 30;
  const settings = {
    ...(socialViewMode ? { socialViewMode } : {}),
    ...(profileVisibility ? { profileVisibility } : {}),
    ...(membershipVisibility ? { membershipVisibility } : {}),
    importTwitchScheduleToSupportPlan,
    ...(form.settings && ['none','once','all'].includes(form.settings.messageEmailNotifyMode) ? { messageEmailNotifyMode: form.settings.messageEmailNotifyMode } : { messageEmailNotifyMode: 'once' }),
    session: { stayLoggedIn: sessionStay, autoLogoutMinutes: autoMinutes }
  };
  // Registrierung: bearbeitbare Felder (ohne discoverySource/acceptedTermsAt)
  const regIn = form.settings?.registration || {};
  const regAllowed = {};
  const takeStr = (v) => typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : '');
  if (regIn.firstName !== undefined) regAllowed.firstName = takeStr(regIn.firstName).trim().slice(0,120);
  if (regIn.lastName !== undefined) regAllowed.lastName = takeStr(regIn.lastName).trim().slice(0,120);
  if (regIn.postalCode !== undefined) regAllowed.postalCode = takeStr(regIn.postalCode).trim().slice(0,20);
  if (regIn.city !== undefined) regAllowed.city = takeStr(regIn.city).trim().slice(0,120);
  if (regIn.country !== undefined) regAllowed.country = takeStr(regIn.country).trim().slice(0,120);
  if (regIn.birthdate !== undefined) regAllowed.birthdate = takeStr(regIn.birthdate).trim().slice(0,32);
  if (regIn.twitch !== undefined) regAllowed.twitch = takeStr(regIn.twitch).trim().slice(0,120);
  if (regIn.tiktok !== undefined) regAllowed.tiktok = takeStr(regIn.tiktok).trim().slice(0,120);
  if (regIn.youtube !== undefined) regAllowed.youtube = takeStr(regIn.youtube).trim().slice(0,120);
  if (regIn.instagram !== undefined) regAllowed.instagram = takeStr(regIn.instagram).trim().slice(0,120);
  if (Object.keys(regAllowed).length > 0) {
    settings.registration = regAllowed;
  }
  const res = await api.patch("/admin/profile", { ...form, avatarUrl, socialLinks: socialLinksToSave, settings });
      onSave && onSave(res.data.user);
      // Cache-Busting: Zeitstempel an avatarUrl
      // Globaler User: nur ROHEN Pfad/URL speichern (ohne Cache-Bust, ohne künstliches Präfix)
      const rawAvatar = res.data.user.avatarUrl || '';
      setUser({ ...res.data.user, avatarUrl: rawAvatar });
      // Update form state with returned user so fields remain populated
      setForm({
        displayName: res.data.user.displayName || '',
        bio: res.data.user.bio || '',
        avatarUrl: rawAvatar,
        socialLinks: Array.isArray(res.data.user.socialLinks) ? res.data.user.socialLinks : [],
        settings: typeof res.data.user.settings === 'object' && res.data.user.settings ? res.data.user.settings : {}
      });
      let msg = "Profil erfolgreich gespeichert!";
      if (faviconMsg) msg += '\n' + faviconMsg;
      setSuccess(msg);
      setAvatarFile(null);
      setAvatarPreview(rawAvatar ? toPreviewUrl(rawAvatar) : '');
    } catch (e) {
      setError(e.response?.data?.error || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const requestDeletion = async () => {
    if (!deleteChecked) return;
    setDeleteBusy(true);
    try {
      const res = await api.post('/users/me/request-deletion');
      const hours = res?.data?.expiresHours || 24;
      setSuccess(`Löschung angefordert. Du erhältst gleich eine E‑Mail mit einem Bestätigungslink.\nDu hast ${hours} Stunden Zeit, die Löschung final zu bestätigen.`);
      setDeleteOpen(false);
      setDeleteChecked(false);
    } catch (e) {
      setError(e?.response?.data?.error || 'Anfrage fehlgeschlagen');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="home-page">
      {deleteOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => { if (!deleteBusy) { setDeleteOpen(false); setDeleteChecked(false); } }}
        >
          <div
            className="highlight-card"
            style={{ maxWidth: 720, width: '100%', padding: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="highlight-title" style={{ marginTop: 0 }}>Profil löschen</h3>
            <p style={{ marginTop: 8 }}>
              Wenn du fortfährst, werden deine Daten unwiederbringlich gelöscht und du bist kein Mitglied des AP‑Networks mehr.
            </p>
            <p style={{ marginTop: 8, opacity: 0.9 }}>
              Hinweis: Du erhältst im nächsten Schritt eine E‑Mail mit einem Bestätigungslink. Du hast 24 Stunden Zeit, die Löschung final zu bestätigen.
            </p>
            <p style={{ marginTop: 8, opacity: 0.9 }}>
              Aus rechtlichen/abrechnungstechnischen Gründen können bei kostenpflichtigen Leistungen einzelne Daten (z. B. Rechnungs-/Buchhaltungsdaten) gespeichert bleiben.
            </p>

            <div style={{ marginTop: 14, padding: '10px 12px', border: '1px solid rgba(255,99,71,0.35)', borderRadius: 12, background: 'rgba(255,99,71,0.08)' }}>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={deleteChecked}
                  onChange={(e) => setDeleteChecked(e.target.checked)}
                  disabled={deleteBusy}
                />
                <span style={{ fontWeight: 800 }}>
                  Ich verstehe, dass dieser Schritt nicht rückgängig gemacht werden kann.
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                className="hero-btn-secondary"
                onClick={() => { if (!deleteBusy) { setDeleteOpen(false); setDeleteChecked(false); } }}
                disabled={deleteBusy}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="hero-btn-primary"
                onClick={requestDeletion}
                disabled={!deleteChecked || deleteBusy}
              >
                {deleteBusy ? 'Sende E‑Mail…' : 'Löschung per E‑Mail bestätigen'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Outer full-width header section */}
      <section id="top" className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Profileinstellungen</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth: 820 }}>
          Passe dein Profil und deine Privatsphäre an – ohne dass sich Inhalte oder Funktionen ändern.
        </p>
      </section>

      <section style={{ width:'100%', maxWidth: 980, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <form onSubmit={handleSubmit} className="edit-profile-form" style={{ display:'grid', gap: 14 }}>
          {success && (
            <div style={{
              borderRadius: 12,
              padding: '12px 14px',
              whiteSpace: 'pre-line',
              border: '1px solid rgba(50,205,137,0.45)',
              background: 'rgba(50,205,137,0.10)',
              color: '#9FE870',
              fontWeight: 800,
            }}>
              {success}
            </div>
          )}

          {error && (
            <div style={{
              borderRadius: 12,
              padding: '12px 14px',
              whiteSpace: 'pre-line',
              border: '1px solid rgba(255,99,71,0.45)',
              background: 'rgba(255,99,71,0.10)',
              color: 'tomato',
              fontWeight: 800,
            }}>
              {error}
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
            {/* Profil */}
            <div style={cardStyle}>
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap: 10, marginBottom: 10 }}>
                <h3 style={sectionTitleStyle}>Profil</h3>
              </div>

              <div style={{ display:'grid', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Display Name</label>
                  <input
                    type="text"
                    value={form.displayName}
                    onChange={e => handleChange('displayName', e.target.value)}
                    maxLength={32}
                    style={inputStyle}
                    placeholder="Anzeigename"
                  />
                </div>

                <div>
                  <label style={labelStyle}>Bio</label>
                  <textarea
                    value={form.bio}
                    onChange={e => handleChange('bio', e.target.value)}
                    rows={4}
                    maxLength={300}
                    style={textareaStyle}
                    placeholder="Kurzbeschreibung (max. 300 Zeichen)"
                  />
                </div>

                <div style={subtleDivider} />

                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 10, marginBottom: 6 }}>
                    <div style={{ fontWeight: 800 }}>Avatar</div>
                    {avatarPreview && (
                      <button type="button" onClick={handleAvatarRemove} style={{ background:'none', border:0, cursor:'pointer', color:'tomato', fontWeight:800 }}>
                        Entfernen
                      </button>
                    )}
                  </div>

                  <div
                    onDrop={handleAvatarDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => document.getElementById('avatarFileInput').click()}
                    style={{
                      border: '1px dashed rgba(255,255,255,0.28)',
                      borderRadius: 14,
                      padding: 14,
                      cursor: 'pointer',
                      background: 'rgba(255,255,255,0.04)',
                      display: 'grid',
                      placeItems: 'center',
                      gap: 10,
                      textAlign: 'center'
                    }}
                  >
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Avatar Preview"
                        style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: '50%', border:'1px solid rgba(255,255,255,0.14)' }}
                      />
                    ) : (
                      <div style={{ opacity: 0.85 }}>
                        Bild hierher ziehen oder klicken zum Auswählen
                      </div>
                    )}
                    <input id="avatarFileInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarFile} />
                  </div>

                  <div style={{ display:'grid', gap: 6, marginTop: 10 }}>
                    <div style={hintStyle}>Max. 2MB, JPG/PNG/GIF/WEBP</div>
                    <div style={hintStyle}>Alternativ: Avatar-URL eingeben</div>
                    <input
                      type="text"
                      value={form.avatarUrl}
                      onChange={e => handleChange('avatarUrl', e.target.value)}
                      placeholder="https://…"
                      style={inputStyle}
                    />
                    {avatarError && <div style={{ color: 'tomato', fontSize: 13, fontWeight: 700 }}>{avatarError}</div>}
                  </div>
                </div>

                <div style={subtleDivider} />

                {/* Registrierung / Angaben */}
                <div>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Deine Angaben</div>
                  <div style={{ display:'grid', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Vorname</label>
                      <input
                        type="text"
                        value={form.settings?.registration?.firstName || ''}
                        onChange={e => setForm(f => ({
                          ...f,
                          settings: {
                            ...(f.settings || {}),
                            registration: { ...(f.settings?.registration || {}), firstName: e.target.value }
                          }
                        }))}
                        maxLength={120}
                        placeholder="z. B. Alex"
                        style={inputStyle}
                      />
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={labelStyle}>Twitch‑Name</label>
                        <input
                          type="text"
                          value={form.settings?.registration?.twitch || ''}
                          onChange={e => setForm(f => ({
                            ...f,
                            settings: {
                              ...(f.settings || {}),
                              registration: { ...(f.settings?.registration || {}), twitch: e.target.value }
                            }
                          }))}
                          maxLength={120}
                          placeholder="ohne @"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>TikTok‑Name</label>
                        <input
                          type="text"
                          value={form.settings?.registration?.tiktok || ''}
                          onChange={e => setForm(f => ({
                            ...f,
                            settings: {
                              ...(f.settings || {}),
                              registration: { ...(f.settings?.registration || {}), tiktok: e.target.value }
                            }
                          }))}
                          maxLength={120}
                          placeholder="ohne @"
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={labelStyle}>YouTube‑Name</label>
                        <input
                          type="text"
                          value={form.settings?.registration?.youtube || ''}
                          onChange={e => setForm(f => ({
                            ...f,
                            settings: {
                              ...(f.settings || {}),
                              registration: { ...(f.settings?.registration || {}), youtube: e.target.value }
                            }
                          }))}
                          maxLength={120}
                          placeholder="Kanal oder Handle"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Instagram‑Name</label>
                        <input
                          type="text"
                          value={form.settings?.registration?.instagram || ''}
                          onChange={e => setForm(f => ({
                            ...f,
                            settings: {
                              ...(f.settings || {}),
                              registration: { ...(f.settings?.registration || {}), instagram: e.target.value }
                            }
                          }))}
                          maxLength={120}
                          placeholder="ohne @"
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Einstellungen */}
            <div style={cardStyle}>
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap: 10, marginBottom: 10 }}>
                <h3 style={sectionTitleStyle}>Einstellungen</h3>
              </div>

              <div style={{ display:'grid', gap: 14 }}>
                <div>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Streamplan</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={!!form.settings?.importTwitchScheduleToSupportPlan}
                      onChange={e => setForm(f => ({
                        ...f,
                        settings: { ...(f.settings || {}), importTwitchScheduleToSupportPlan: e.target.checked }
                      }))}
                    />
                    <span>Twitch‑Streamplan automatisch in unseren Streamplan übernehmen</span>
                  </label>
                  <div style={{ ...hintStyle, marginTop: 6 }}>
                    Wenn aktiv, werden Einträge aus deinem Twitch‑Streamplan künftig automatisch übernommen.
                  </div>
                </div>

                <div style={subtleDivider} />

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Darstellung der Social‑Inhalte</div>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                    <input
                      type="radio"
                      name="socialViewMode"
                      value="combined"
                      checked={(form.settings?.socialViewMode || 'combined') === 'combined'}
                      onChange={() => setForm(f => ({ ...f, settings: { ...(f.settings || {}), socialViewMode: 'combined' } }))}
                    />
                    <span>Kombiniert: immer die 3 neuesten pro Plattform (über alle Kanäle)</span>
                  </label>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <input
                      type="radio"
                      name="socialViewMode"
                      value="per-channel"
                      checked={(form.settings?.socialViewMode || 'combined') === 'per-channel'}
                      onChange={() => setForm(f => ({ ...f, settings: { ...(f.settings || {}), socialViewMode: 'per-channel' } }))}
                    />
                    <span>Nach Kanal: je Kanal/Profil die 3 neuesten</span>
                  </label>
                  <div style={{ ...hintStyle, marginTop: 6 }}>Hinweis: Diese Einstellung gilt für dein Profil für alle Besucher.</div>
                </div>

                <div style={subtleDivider} />

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Sichtbarkeit</div>
                  <div style={{ display:'grid', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>Wer darf dein Profil sehen?</label>
                      <select
                        value={form.settings?.profileVisibility || 'public'}
                        onChange={e => setForm(f => ({ ...f, settings: { ...(f.settings || {}), profileVisibility: e.target.value } }))}
                        style={selectStyle}
                      >
                        <option value="public">Jeder (Öffentlich)</option>
                        <option value="members">Mitglieder (Intern)</option>
                        <option value="admin">Niemand (Admin-Only)</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Wer darf deine Mitgliedschaft im AP‑Network sehen?</label>
                      <select
                        value={form.settings?.membershipVisibility || 'public'}
                        onChange={e => setForm(f => ({ ...f, settings: { ...(f.settings || {}), membershipVisibility: e.target.value } }))}
                        style={selectStyle}
                      >
                        <option value="public">Jeder (Öffentlich)</option>
                        <option value="members">Mitglieder (Intern)</option>
                        <option value="admin">Niemand (Admin-Only)</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ ...hintStyle, marginTop: 6 }}>Standard ist „Jeder (Öffentlich)“.</div>
                </div>

                <div style={subtleDivider} />

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Sitzung</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={!!(form.settings?.session?.stayLoggedIn)}
                      onChange={e => setForm(f => ({
                        ...f,
                        settings: {
                          ...(f.settings || {}),
                          session: { ...(f.settings?.session || {}), stayLoggedIn: e.target.checked }
                        }
                      }))}
                    />
                    <span>Eingeloggt bleiben (verlängerte Sitzung bis zu 30 Tage)</span>
                  </label>
                  <div>
                    <label style={labelStyle}>Automatischer Logout nach Minuten Inaktivität (wenn nicht aktiviert „Eingeloggt bleiben“)</label>
                    <input
                      type="number"
                      min={5}
                      max={1440}
                      value={form.settings?.session?.autoLogoutMinutes || 30}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        setForm(f => ({
                          ...f,
                          settings: {
                            ...(f.settings || {}),
                            session: { ...(f.settings?.session || {}), autoLogoutMinutes: val }
                          }
                        }));
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ ...hintStyle, marginTop: 6 }}>
                    Bei aktivierter Option „Eingeloggt bleiben“ wird kein automatischer Logout nach Inaktivität durchgeführt.
                  </div>
                </div>

                <div style={subtleDivider} />

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Benachrichtigungen (Private Nachrichten per E‑Mail)</div>
                  <label style={labelStyle}>Modus</label>
                  <select
                    value={(form.settings?.messageEmailNotifyMode && ['none', 'once', 'all'].includes(form.settings.messageEmailNotifyMode)) ? form.settings.messageEmailNotifyMode : 'once'}
                    onChange={e => setForm(f => ({ ...f, settings: { ...(f.settings || {}), messageEmailNotifyMode: e.target.value } }))}
                    style={selectStyle}
                  >
                    <option value="none">Keine E-Mail (nie)</option>
                    <option value="once">Einmal pro Offline-Phase (Standard)</option>
                    <option value="all">Für jede neue Nachricht (wenn offline)</option>
                  </select>
                  <div style={{ ...hintStyle, marginTop: 6 }}>
                    „Offline“ bedeutet: keine aktive WebSocket‑Verbindung. Bei „Einmal“ erhältst du nach erneutem Login wieder eine Mail für die erste neue Nachricht.
                    Bei „Alle“ werden mehrere E‑Mails gesendet, wenn du viele Nachrichten offline bekommst.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div style={cardStyle}>
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap: 10, marginBottom: 10 }}>
              <h3 style={sectionTitleStyle}>Social Links</h3>
              <button type="button" onClick={handleAddSocial} className="hero-btn-secondary" style={{ padding: '10px 14px', borderRadius: 12, fontWeight: 800 }}>
                + Hinzufügen
              </button>
            </div>

            {form.socialLinks.length === 0 ? (
              <div style={hintStyle}>Noch keine Social‑Links hinzugefügt.</div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {form.socialLinks.map((link, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12,
                      padding: 12,
                      display: 'grid',
                      gap: 10,
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      <input
                        type="text"
                        value={link.name}
                        onChange={e => handleSocialChange(idx, 'name', e.target.value)}
                        placeholder="Name (z.B. Instagram)"
                        maxLength={20}
                        style={inputStyle}
                      />
                      <input
                        type="text"
                        value={link.url}
                        onChange={e => handleSocialChange(idx, 'url', e.target.value)}
                        placeholder="https://…"
                        maxLength={120}
                        style={inputStyle}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: 0.9, whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={!!link.useFavicon}
                          onChange={e => handleSocialChange(idx, 'useFavicon', e.target.checked)}
                        />
                        Favicon
                      </label>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        {link.useFavicon && link.url && (
                          <button
                            type="button"
                            className="hero-btn-secondary"
                            onClick={async () => {
                              try {
                                setLoading(true);
                                const res = await api.post('/admin/profile/favicon-refresh', { idx, url: link.url, force: true });
                                if (res?.data?.user?.socialLinks) {
                                  setForm(f => ({ ...f, socialLinks: res.data.user.socialLinks }));
                                  setSuccess('Favicon aktualisiert.');
                                } else {
                                  setSuccess('Favicon aktualisiert.');
                                }
                              } catch (e) {
                                setError(e?.response?.data?.error || 'Favicon-Aktualisierung fehlgeschlagen');
                              } finally {
                                setLoading(false);
                              }
                            }}
                            style={{ padding: '10px 12px', borderRadius: 12, fontWeight: 800 }}
                            title="Favicon neu laden"
                          >
                            Aktualisieren
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => handleRemoveSocial(idx)}
                          style={{ background: 'none', border: 0, color: 'tomato', cursor: 'pointer', fontWeight: 900, fontSize: 18, padding: '6px 8px', lineHeight: 1 }}
                          title="Entfernen"
                          aria-label="Social-Link entfernen"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ ...hintStyle, marginTop: 10 }}>
              Tipp: Wenn „Favicon“ aktiv ist, versucht das System ein Plattform‑Icon automatisch zu übernehmen.
            </div>
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={loading}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                color: 'rgba(255,99,71,0.9)',
                fontWeight: 800,
                fontSize: 13,
                textDecoration: 'underline',
              }}
            >
              Profil löschen
            </button>

            <button type="submit" className="hero-btn-primary" disabled={loading} style={{ minWidth: 220 }}>
              {loading ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
